// The next session and Book a session, side by side as ClientSpace.dc.html draws them, with Adam's
// boxes in his view: the booking link (the board's) and the next session's date, time, length and
// Zoom link (no board draws it: the critic's settlement and sign-off question 5, "Adam can type a
// session in", so the client's next session never needs Calendly read, decision 36).
//
// The client sees the card only while a session is still to come; with none, Book a session fills
// the row. Join opens the session's Zoom link in a new tab from the product's join_opens_minutes
// before it starts until it ends, as the Library's Next up does (stage 3 item 60).
import { useEffect, useState } from 'react'
import { downloadIcs } from '../../lib/calendar.js'
import { COPY } from './copy.js'
import { bookingLink, ukParts, ukToIso, LENGTHS, lengthLabel } from './model.js'
import { saveBookingUrl, saveNextSession } from './data.js'

export function SessionRow({ ctx }) {
  const { next, staff, open } = ctx
  const showNext = open && (next || staff)
  return (
    <section className={showNext ? 'ms-row' : 'ms-row ms-row--single'} data-part="next">
      {showNext && <NextCard ctx={ctx} />}
      <BookCard ctx={ctx} />
    </section>
  )
}

function NextCard({ ctx }) {
  const { next, staff, product } = ctx
  const [added, setAdded] = useState(false)

  function addCal() {
    downloadIcs({ uid: next.session.id, title: `${product.name}, ${next.title}`, startMs: next.start, endMs: next.end, path: product.path || '/' })
    setAdded(true)
  }

  return (
    <div className="ms-next">
      <span className="ms-next-label">{COPY.nextLabel}</span>
      {next ? (
        <>
          <span className="ms-next-when">{next.when}</span>
          <span className="ms-next-line">{next.zoom ? COPY.nextOnZoom(next.title) : next.title}</span>
          <div className="ms-next-actions">
            {next.zoom && !next.joinOpen && <span className="ms-chip">{COPY.joinOpensAt(next.joinTime)}</span>}
            {next.zoom && next.joinOpen && (
              <a className="ms-join" href={next.zoom} target="_blank" rel="noopener noreferrer"
                aria-label={COPY.joinAria(product.name, next.title)}>{COPY.join}</a>
            )}
            <button type="button" className="ms-cal" onClick={addCal}>{added ? COPY.addedCal : COPY.addCal}</button>
          </div>
        </>
      ) : (
        <>
          <span className="ms-next-when">{COPY.notBooked}</span>
          <span className="ms-next-line">{COPY.notBookedLine}</span>
        </>
      )}
      {staff && <NextBox ctx={ctx} />}
    </div>
  )
}

// Adam's next session: change the 1 still to come, or add the next
function NextBox({ ctx }) {
  const { next, space, sessions, first, patch, nowMs } = ctx
  const existing = next?.session || null
  const startParts = ukParts(existing?.starts_at)
  const [date, setDate] = useState(startParts.date)
  const [time, setTime] = useState(startParts.time || '19:00')
  const [minutes, setMinutes] = useState(existing?.duration_minutes || 60)
  const [zoom, setZoom] = useState(existing?.zoom_join_url || '')
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)
  const lengths = LENGTHS.includes(minutes) ? LENGTHS : [...LENGTHS, minutes].sort((a, b) => a - b)

  async function save(e) {
    e.preventDefault()
    if (busy) return
    const startsAt = ukToIso(date, time)
    if (!startsAt) { setMsg({ bad: true, text: COPY.nextNeedWhen }); return }
    if (Date.parse(startsAt) + minutes * 60000 <= nowMs) { setMsg({ bad: true, text: COPY.nextPast }); return }
    const link = zoom.trim()
    if (link && !/^https:\/\/./i.test(link)) { setMsg({ bad: true, text: COPY.nextBadZoom }); return }
    setBusy(true)
    setMsg(null)
    try {
      const highest = sessions.reduce((m, s) => Math.max(m, s.number), 0)
      const row = await saveNextSession({ spaceId: space.id, existing, startsAt, minutes, zoom: link, highest })
      patch(d => ({ ...d, sessions: [...d.sessions.filter(s => s.id !== row.id), row].sort((a, b) => a.number - b.number) }))
      setMsg({ bad: false, text: COPY.nextSaved(first) })
    } catch (err) {
      setMsg({ bad: true, text: err?.code === '23514' ? COPY.nextBadZoom : COPY.notSaved })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="ms-box ms-box--dark" onSubmit={save} noValidate>
      <span className="ms-box-label">{COPY.nextBox}</span>
      <div className="ms-next-fields">
        <label className="ms-field">{COPY.nextDate}<input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
        <label className="ms-field">{COPY.nextTime}<input type="time" value={time} step="300" onChange={e => setTime(e.target.value)} /></label>
        <label className="ms-field">{COPY.nextLength}
          <select value={minutes} onChange={e => setMinutes(Number(e.target.value))}>
            {lengths.map(m => <option key={m} value={m}>{lengthLabel(m)}</option>)}
          </select>
        </label>
        <label className="ms-field ms-field--wide">{COPY.nextZoom}
          <input type="url" inputMode="url" value={zoom} placeholder="https://zoom.us/j/..." onChange={e => setZoom(e.target.value)} />
        </label>
      </div>
      <div className="ms-box-foot">
        <button type="submit" className="ms-btn-light" disabled={busy}>{existing ? COPY.nextSave : COPY.nextAdd}</button>
        {msg && <span className="ms-box-msg" role={msg.bad ? 'alert' : 'status'}>{msg.text}</span>}
      </div>
    </form>
  )
}

function BookCard({ ctx }) {
  const { space, client, staff, open } = ctx
  const link = bookingLink(space.booking_url, client?.full_name, client?.email)
  if (!open) return null
  return (
    <div className="ms-book">
      <span className="ms-card-label">{COPY.bookLabel}</span>
      <span className="ms-book-line">{COPY.bookLine}</span>
      {link && <a className="ms-btn" href={link} target="_blank" rel="noopener noreferrer">{COPY.bookButton}</a>}
      <span className="ms-book-note">{COPY.bookNote}</span>
      {staff && <BookingBox ctx={ctx} />}
    </div>
  )
}

// "Booking link, Adam only": saved when he leaves the box or presses Enter
function BookingBox({ ctx }) {
  const { space, patch } = ctx
  const [value, setValue] = useState(space.booking_url)
  const [msg, setMsg] = useState(null)
  useEffect(() => { setValue(space.booking_url) }, [space.booking_url])

  async function save() {
    const url = value.trim()
    if (url === space.booking_url) return
    if (!/^https:\/\/[^\s/]+\.[^\s]+$/i.test(url)) { setMsg({ bad: true, text: COPY.bookingBad }); return }
    setMsg(null)
    try {
      await saveBookingUrl(space.id, url)
      patch(d => ({ ...d, space: { ...d.space, booking_url: url } }))
      setMsg({ bad: false, text: COPY.bookingSaved })
    } catch (err) {
      setMsg({ bad: true, text: err?.code === '23514' ? COPY.bookingBad : COPY.notSaved })
    }
  }

  return (
    <div className="ms-box">
      <label className="ms-box-label ms-box-label--field">{COPY.bookingBox}
        <input type="url" inputMode="url" value={value} onChange={e => setValue(e.target.value)} onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save() } }} />
      </label>
      {msg && <span className="ms-box-msg" role={msg.bad ? 'alert' : 'status'}>{msg.text}</span>}
    </div>
  )
}
